import { KeyboardArrowLeft as KeyboardArrowLeftIcon } from '@mui/icons-material';
import { alpha, Box, Button, CircularProgress, Fade, MobileStepper, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useCelloData } from '../../../hooks/useCelloData';
import { useTranslation } from '../../../hooks/useTranslation';
import { useCelloService } from '../../../services/cello/cello-api-service'; // Added for Cello
import { useGoogleChatNotificationService } from '../../../services/googleChatNotification.service';
import { useIntegrationService } from '../../../services/integration.service';
import { useUserService } from '../../../services/user.service';

// Define the slides content
const getSlides = (t: (key: string) => string) => [
    {
        label: t('onboarding.final.slides.dashboard.title'),
        description: t('onboarding.final.slides.dashboard.description'),
        key: 'dashboard',
        // imgPath:
        // 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iIzU1NSI+RGFzaGJvYXJkPC90ZXh0Pjwvc3ZnPg==',
        imgPath: '/onboarding/dashboard.png',
    },
    {
        label: t('onboarding.final.slides.integrations.title'),
        description: t('onboarding.final.slides.integrations.description'),
        key: 'integrations',
        // imgPath:
        // 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iIzU1NSI+VHJhbnNhY3Rpb25zPC90ZXh0Pjwvc3ZnPg==',
        imgPath: '/onboarding/integrations.png',
    },
    {
        label: t('onboarding.final.slides.taxReports.title'),
        description: t('onboarding.final.slides.taxReports.description'),
        key: 'taxReports',
        // imgPath:
        // 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIyNCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgZmlsbD0iIzU1NSI+VGF4IFJlcG9ydHM8L3RleHQ+PC9zdmc+',
        imgPath: '/onboarding/taxreports.png',
    },
];

interface FinalStepProps {
    onNext: () => void;
    onBack: () => void;
}

const FinalStep: React.FC<FinalStepProps> = ({ onNext, onBack }) => {
    const { currentUser, currentCtUser, refreshToken } = useAuth(); // Added currentUser, currentCtUser for Cello
    const userService = useUserService();
    const { sendRegistrationNotification } = useGoogleChatNotificationService();
    const celloApiService = useCelloService(); // Added for Cello
    const { data: ucc, isLoading: _isCelloDataLoading, error: _celloDataError } = useCelloData(); // Renamed isLoading and error to avoid naming conflicts, prefixed with _ as unused
    const integrationService = useIntegrationService();
    const { t } = useTranslation();
    const slides = getSlides(t);
    const theme = useTheme();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const maxSteps = slides.length;
    const [isLoading, setIsLoading] = useState(false);

    // Handle next button click
    const handleNext = async () => {
        if (activeStep === maxSteps - 1) {
            // If we're on the last slide, go to dashboard
            setIsLoading(true);
            try {
                // --- Cello Integration: Start ---
                if (ucc && typeof ucc === 'string' && ucc.trim() !== '') {
                    try {
                        console.log(`[FinalStep] UCC found: ${ucc}. Calling completeSignupProcess.`);
                        if (currentUser && currentCtUser) {
                            // Ensure user data is available
                            await celloApiService.completeSignupProcess(
                                ucc,
                                currentUser.uid,
                                currentUser.email || '',
                                currentUser.displayName || '',
                                currentCtUser.currency || 'EUR'
                            );
                            console.log('[FinalStep] completeSignupProcess call successful.');
                        } else {
                            console.warn(
                                '[FinalStep] currentUser or currentCtUser not available. Skipping completeSignupProcess.'
                            );
                        }
                    } catch (celloError) {
                        console.error('[FinalStep] Error during Cello completeSignupProcess call:', celloError);
                        // Decide if this error should prevent onboarding completion. For now, it continues.
                    }
                } else {
                    console.log('[FinalStep] No valid UCC found from useCelloData. Skipping completeSignupProcess.');
                }
                // --- Cello Integration: End ---

                // Complete onboarding (reverted correction, actual name is finihsOnboarding)
                await userService.finihsOnboarding();
                await sendRegistrationNotification();
                await refreshToken();

                // Call onNext to navigate to plans page
                onNext();
            } catch (error) {
                console.error('Error completing onboarding:', error);
            } finally {
                setIsLoading(false);
            }
        } else {
            // Otherwise, go to the next slide
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                gap: 3,
                width: '100%',
                maxWidth: 800,
                margin: '0 auto',
            }}
        >
            <Typography variant="h5" component="h2" fontWeight={600}>
                {t('onboarding.final.title')}
            </Typography>

            <Typography variant="body1" color="text.secondary">
                {t('onboarding.final.description')}
            </Typography>

            {/* Slider container */}
            <Box
                sx={{
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    backgroundColor:
                        theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.8)' : theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                    flex: 1,
                }}
            >
                {/* Slides */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        position: 'relative',
                    }}
                >
                    {slides.map((slide, index) => (
                        <Fade
                            key={slide.key}
                            in={activeStep === index}
                            timeout={500}
                            style={{
                                display: activeStep === index ? 'flex' : 'none',
                                flexDirection: 'column',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    justifyContent: 'flex-start',
                                    padding: 2,
                                    paddingBottom: 0,
                                    height: '100%',
                                }}
                            >
                                {/* Header with title and stepper dots on the same line */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'left',
                                        width: '100%',
                                        marginBottom: 1,
                                    }}
                                >
                                    <Typography variant="h6">{slide.label}</Typography>

                                    {/* Stepper dots aligned to the right */}
                                    <MobileStepper
                                        steps={maxSteps}
                                        position="static"
                                        activeStep={activeStep}
                                        sx={{
                                            backgroundColor: 'transparent',
                                            padding: 0,
                                            '& .MuiMobileStepper-dot': {
                                                margin: '0 4px',
                                                backgroundColor:
                                                    theme.palette.mode === 'dark'
                                                        ? 'rgba(255, 255, 255, 0.3)'
                                                        : 'rgba(0, 0, 0, 0.2)',
                                            },
                                            '& .MuiMobileStepper-dotActive': {
                                                backgroundColor: theme.palette.primary.main,
                                            },
                                        }}
                                        nextButton={null}
                                        backButton={null}
                                    />
                                </Box>

                                {/* Description text aligned to the left */}
                                <Box
                                    sx={{
                                        width: '100%',
                                        textAlign: 'left',
                                        marginBottom: 1,
                                    }}
                                >
                                    <Typography variant="body1" color="text.secondary">
                                        {t(`onboarding.final.slides.${slide.key}.descriptionShort`)}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        height: '100%', // Use remaining height
                                        width: '100%',
                                        overflow: 'hidden',
                                        flexGrow: 1, // Allow this box to grow and fill space
                                        display: 'flex',
                                        alignItems: 'flex-start', // Align to top
                                    }}
                                >
                                    <Box
                                        component="img"
                                        sx={{
                                            width: '100%',
                                            height: 'auto',
                                            objectFit: 'cover', // Cover the container
                                            objectPosition: 'top', // Show the top part of the image
                                            borderRadius: 1, // Slight rounded corners
                                        }}
                                        src={slide.imgPath}
                                        alt={slide.label}
                                    />
                                </Box>
                            </Box>
                        </Fade>
                    ))}
                </Box>
            </Box>

            {/* Navigation buttons */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 'auto',
                    paddingTop: 2,
                }}
            >
                <Button
                    onClick={onBack}
                    variant="outlined"
                    startIcon={<KeyboardArrowLeftIcon />}
                    sx={{
                        minWidth: 100,
                        borderColor: (theme) => alpha(theme.palette.divider, 0.8),
                        color: 'text.primary',
                        '&:hover': {
                            borderColor: (theme) => theme.palette.divider,
                            backgroundColor: (theme) => alpha(theme.palette.action.hover, 0.1),
                        },
                    }}
                >
                    {t('onboarding.final.back')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={isLoading}
                    sx={{
                        minWidth: 150,
                    }}
                >
                    {isLoading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : activeStep === maxSteps - 1 ? (
                        t('onboarding.final.finish')
                    ) : (
                        t('onboarding.final.next')
                    )}
                </Button>
            </Box>
        </Box>
    );
};

export default FinalStep;
